import { useEffect, useId, useRef, useState } from 'react';

import { cn } from '@rocco/utils/cn';

/**
 * The logomark's artwork as a mask, not as a drawing.
 *
 * `backdrop-filter` applies to a box, so an inline `<svg>` cannot refract the
 * photograph through the letterform — it would frost the whole rectangle the
 * glyph sits in. Masking a plain div to this shape is what confines the effect
 * to the letter itself. Same paths as `logo-new-black.svg`, flattened to one
 * line and forced to a solid fill: a mask reads coverage, not colour.
 *
 * Base64 rather than a percent-encoded SVG: the artwork carries a `#` in its
 * fill, which terminates a bare `data:` URI and leaves `mask-image: none` — a
 * frosted rectangle where the letter should be.
 */
const LOGOMARK_MASK =
    'url("data:image/svg+xml;base64,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")';

const MASK_STYLE = {
    WebkitMaskImage: LOGOMARK_MASK,
    maskImage: LOGOMARK_MASK,
    WebkitMaskRepeat: 'no-repeat',
    maskRepeat: 'no-repeat',
    WebkitMaskSize: 'contain',
    maskSize: 'contain',
    WebkitMaskPosition: 'center',
    maskPosition: 'center',
} as const;

/**
 * How fast the mark travels against the page: 0 holds it still with the fixed
 * header, 1 scrolls it with the copy. Between the banner's own drift and the
 * text, so the three separate as the reader scrolls.
 */
const DRIFT_RATE = 0.4;

/** The map is generated at this size, then stretched to the element. */
const MAP_W = 280;
const MAP_H = 216;

/**
 * How far, in pixels, the most-displaced part of the bezel bends the picture
 * behind it. Negative because the ramp runs from the outside in: the glyph's
 * edge should pull the background outward, the way the rim of a lens does.
 *
 * 70 is where the open web implementations of this effect sit, and it holds
 * here: the letterform's strokes are thin, so a timid displacement is lost
 * inside them and reads as a blur.
 */
const REFRACTION = -70;

/**
 * How steeply the lens falls away from the edge.
 *
 * Above 1 the displacement concentrates in the outermost sliver of the bezel
 * and the middle of a stroke stays still — which is what a lens actually does,
 * and what separates this from a uniform smear. It is the cheap stand-in for
 * the Snell's-law profile a full implementation integrates: same shape, no
 * ray tracing.
 */
const BEZEL_FALLOFF = 1.7;

/** Where the light is. Up and slightly left, as the rest of the page assumes. */
const LIGHT = { x: -0.55, y: -0.84 };

/**
 * Every number the material is made of, so a tuning page can vary them and
 * the header can ignore them. Defaults are the shipped values.
 */
export interface GlassTuning {
    /** Peak displacement in pixels. */
    refraction?: number;
    /** Bezel width, as the blur radius the coverage field is built from. */
    bezel?: number;
    /** How steeply the lens falls away from the edge. */
    falloff?: number;
    /** Spread between the three channels' displacement, in pixels. */
    aberration?: number;
    /** Blur applied to the refracted image, in pixels. */
    blur?: number;
    /** Saturation of the refracted image. */
    saturate?: number;
    /** Fill over the letterform, 0-1. */
    tint?: number;
    /**
     * Colour of that fill. Apple's own glass is rarely neutral — it picks up a
     * cool cast from the surface it is lit by — so this is not always white.
     */
    tintColor?: string;
    /** Strength of the lit rim, 0-1. */
    rim?: number;
    /** Strength of the shaded rim, 0-1. */
    shade?: number;
}

const DEFAULTS: Required<GlassTuning> = {
    refraction: REFRACTION,
    bezel: 5,
    falloff: BEZEL_FALLOFF,
    aberration: 4,
    blur: 0.4,
    saturate: 1.4,
    tint: 0.12,
    tintColor: '255,255,255',
    rim: 1,
    shade: 1,
};

/**
 * Build the displacement map, and the specular pass that lights its rim.
 *
 * A displacement map is not a picture — it is a field of vectors, one per
 * pixel, encoded as colour: red carries the horizontal offset and green the
 * vertical, with 128 meaning "do not move". `feDisplacementMap` reads those
 * two channels and pushes the backdrop around accordingly.
 *
 * The vectors come from the glyph's own shape. Blurring the mask's alpha gives
 * a field that ramps from 0 outside the letter to 1 well inside it; the
 * gradient of that field points inward and peaks exactly at the edge, which is
 * where a real bezel is steepest and bends light hardest. Flat interiors have
 * no gradient, so the middle of a stroke stays undisplaced — as it should:
 * glass is only interesting at its edges.
 */
const buildMaps = async (
    t: Required<GlassTuning>,
): Promise<{
    map: string;
    light: string;
    shade: string;
} | null> => {
    const image = new Image();
    image.src = LOGOMARK_MASK.slice(5, -2);
    try {
        await image.decode();
    } catch {
        return null;
    }

    const shape = document.createElement('canvas');
    shape.width = MAP_W;
    shape.height = MAP_H;
    const sctx = shape.getContext('2d');
    if (!sctx) return null;
    // The blur is the bezel: its radius is how wide the curved lip reads.
    // Narrow on purpose. A wide blur turns the whole of a thin stroke into
    // bezel, and a letterform that is all edge displaces uniformly — the
    // smear this replaced.
    sctx.filter = `blur(${t.bezel}px)`;
    sctx.drawImage(image, 0, 0, MAP_W, MAP_H);

    const alpha = sctx.getImageData(0, 0, MAP_W, MAP_H).data;
    const at = (x: number, y: number) => {
        const cx = Math.min(MAP_W - 1, Math.max(0, x));
        const cy = Math.min(MAP_H - 1, Math.max(0, y));
        return (alpha[(cy * MAP_W + cx) * 4 + 3] ?? 0) / 255;
    };

    const map = document.createElement('canvas');
    map.width = MAP_W;
    map.height = MAP_H;
    const mctx = map.getContext('2d');
    const light = document.createElement('canvas');
    light.width = MAP_W;
    light.height = MAP_H;
    const lctx = light.getContext('2d');
    const shade = document.createElement('canvas');
    shade.width = MAP_W;
    shade.height = MAP_H;
    const shctx = shade.getContext('2d');
    if (!mctx || !lctx || !shctx) return null;

    const mapData = mctx.createImageData(MAP_W, MAP_H);
    const lightData = lctx.createImageData(MAP_W, MAP_H);
    const shadeData = shctx.createImageData(MAP_W, MAP_H);

    for (let y = 0; y < MAP_H; y += 1) {
        for (let x = 0; x < MAP_W; x += 1) {
            // Two separate things come out of the blurred coverage field, and
            // keeping them apart is what makes this read as a lens rather
            // than a smear.
            //
            // Direction: the slope, by central differences. It points from
            // outside the glyph towards its middle — the inward surface
            // normal of the bezel.
            const gx = (at(x + 1, y) - at(x - 1, y)) * 0.5;
            const gy = (at(x, y + 1) - at(x, y - 1)) * 0.5;
            const len = Math.hypot(gx, gy) || 1;
            const nx = gx / len;
            const ny = gy / len;

            // Strength: how far into the glass this pixel is. Coverage is 0
            // at the outer lip and 1 once the bezel has flattened out, so the
            // profile is strongest at the very edge and dies in the middle of
            // a stroke — which is exactly where a real lens stops bending
            // anything.
            const depth = at(x, y);
            // smoothstep, the ramp the reference implementations use: an
            // S-curve rather than a power curve, so the lens eases in at the
            // lip and eases out in the middle of a stroke instead of starting
            // and stopping abruptly. The exponent still biases how much of the
            // bezel is doing the work.
            const eased = 1 - depth;
            const smooth = eased * eased * (3 - 2 * eased);
            const profile = Math.pow(smooth, t.falloff);
            const dx = nx * profile;
            const dy = ny * profile;

            const i = (y * MAP_W + x) * 4;
            mapData.data[i] = 128 + dx * 127;
            mapData.data[i + 1] = 128 + dy * 127;
            mapData.data[i + 2] = 128;
            mapData.data[i + 3] = 255;

            // The rim light: how squarely this part of the bezel faces the
            // lamp. Raised to a power so it stays a highlight rather than
            // becoming a general brightening of the whole letter.
            const facing = dx * LIGHT.x + dy * LIGHT.y;
            const spec = Math.pow(Math.max(0, facing), 1.6);
            lightData.data[i] = 255;
            lightData.data[i + 1] = 255;
            lightData.data[i + 2] = 255;
            lightData.data[i + 3] = Math.round(spec * 255 * t.rim);

            // And the terminator opposite it. Refraction alone leaves the
            // letter invisible against a busy, mid-toned photograph — nothing
            // in the picture changes brightness, only position. A dark edge
            // where the bezel turns away from the lamp is what gives the
            // glass a silhouette, and it is as physical as the highlight:
            // the same surface, facing the other way.
            const shadeAmt = Math.pow(Math.max(0, -facing), 1.6);
            shadeData.data[i] = 0;
            shadeData.data[i + 1] = 0;
            shadeData.data[i + 2] = 0;
            shadeData.data[i + 3] = Math.round(shadeAmt * 150 * t.shade);
        }
    }

    mctx.putImageData(mapData, 0, 0);
    lctx.putImageData(lightData, 0, 0);
    shctx.putImageData(shadeData, 0, 0);
    return {
        map: map.toDataURL(),
        light: light.toDataURL(),
        shade: shade.toDataURL(),
    };
};

/**
 * The big "A" as a pane of glass over the banner.
 *
 * Not a white wash over a blur — that is frosting, and frosting is what you
 * get when a surface scatters light instead of bending it. This refracts: an
 * SVG filter displaces the backdrop by a vector field built from the glyph's
 * own edges, splits the three channels by a few pixels each so the rim fringes
 * the way real glass disperses, and lays a specular highlight along the edges
 * that face the light. It also drifts as the page scrolls, so the picture
 * behind it bends through a different part of the photograph as it travels.
 *
 * Only Chromium exposes SVG filters to `backdrop-filter`, so everything is
 * layered over a blur-and-wash that stands on its own: where the filter is
 * ignored the mark still reads as glass, just without the refraction.
 */
export const GlassLogomark = ({
    className,
    tuning,
}: {
    className?: string;
    tuning?: GlassTuning;
}) => {
    const t: Required<GlassTuning> = { ...DEFAULTS, ...tuning };
    const rawId = useId();
    const filterId = `glass-mark-${rawId.replace(/:/g, '')}`;
    const [maps, setMaps] = useState<{
        map: string;
        light: string;
        shade: string;
    } | null>(null);
    const alive = useRef(true);
    const driftRef = useRef<HTMLSpanElement>(null);
    const [box, setBox] = useState<{ w: number; h: number } | null>(null);

    // The filter's own <svg> has to carry the element's pixel size.
    //
    // A filter primitive's percentage subregion resolves against the viewport
    // of the SVG that holds the filter — not against the filtered element. The
    // holder here was `size-0`, so `feImage width="100%"` was 100% of nothing:
    // a zero-sized displacement map, and every pixel of the backdrop left
    // exactly where it was. The map was correct the whole time; it was never
    // being sampled.
    useEffect(() => {
        const el = driftRef.current;
        if (!el) return;
        const measure = () => {
            const rect = el.getBoundingClientRect();
            setBox({ w: Math.round(rect.width), h: Math.round(rect.height) });
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    // The drift. The header is fixed, so the mark holds still while the page
    // moves under it; translating it up by a fraction of the scroll makes it
    // travel slower than the copy and faster than the banner behind it.
    // Clamped to one screen so it parallaxes across the banner and then rests
    // rather than climbing away for the length of the article.
    useEffect(() => {
        const el = driftRef.current;
        if (!el) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return;
        }

        let frame = 0;
        const apply = () => {
            frame = 0;
            const travel = Math.min(window.scrollY, window.innerHeight);
            el.style.transform = `translate3d(0, ${-travel * DRIFT_RATE}px, 0)`;
        };
        const onScroll = () => {
            if (frame) return;
            frame = window.requestAnimationFrame(apply);
        };

        apply();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            if (frame) window.cancelAnimationFrame(frame);
            window.removeEventListener('scroll', onScroll);
        };
    }, []);

    useEffect(() => {
        alive.current = true;
        void buildMaps(t).then((built) => {
            if (alive.current) setMaps(built);
        });
        return () => {
            alive.current = false;
        };
        // The tuning object is rebuilt every render; its values are what matter.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [t.bezel, t.falloff, t.rim, t.shade]);

    return (
        <span
            ref={driftRef}
            aria-hidden
            className={cn(
                // Deliberately stretched, like the `<svg>` this replaced: it
                // took the flex column's width and centred a 215px letter
                // inside it. Shrinking the box to the letter moves the letter
                // 60px left of where it has always sat, so the box stays and
                // every layer inside letterboxes to the middle instead —
                // which is exactly what the SVG's default
                // `preserveAspectRatio="xMidYMid meet"` was doing.
                // This element carries the parallax transform, and that is a
                // deliberate trade with the glass below it.
                //
                // Chrome resets the backdrop root at any ancestor carrying a
                // transform — an identity one counts — so the blur and the
                // refraction sample an empty backdrop from here on, and the
                // mark reads as a flat tinted letterform that moves. The
                // filter layers are kept because they cost nothing when they
                // sample nothing, and because removing the drift is all it
                // takes to have the glass back.
                'pointer-events-none relative block',
                className,
            )}
        >
            {maps && box ? (
                <svg
                    className="pointer-events-none absolute top-0 left-0 opacity-0"
                    width={box.w}
                    height={box.h}
                    aria-hidden
                    focusable="false"
                >
                    <filter
                        id={filterId}
                        colorInterpolationFilters="sRGB"
                        // Deliberately larger than the element. The
                        // displacement is strongest at the glyph's edge and
                        // reaches outside it; a region clipped to the box
                        // cuts off the very part of the effect that matters.
                        x="-35%"
                        y="-35%"
                        width="170%"
                        height="170%"
                    >
                        {/* Pinned to the element's own box inside that
                            larger region, so the vectors stay registered with
                            the letterform rather than stretching with the
                            filter area. */}
                        <feImage
                            href={maps.map}
                            x="0%"
                            y="0%"
                            width="100%"
                            height="100%"
                            preserveAspectRatio="xMidYMid meet"
                            result="vectors"
                        />
                        {/* One pass per channel, each displaced a little
                            further than the last, then screened back
                            together — that difference is the dispersion. */}
                        {[0, t.aberration, t.aberration * 2].map(
                            (offset, channel) => (
                                <feDisplacementMap
                                    key={offset}
                                    in="SourceGraphic"
                                    in2="vectors"
                                    scale={t.refraction - offset}
                                    xChannelSelector="R"
                                    yChannelSelector="G"
                                    result={`disp${channel}`}
                                />
                            ),
                        )}
                        <feColorMatrix
                            in="disp0"
                            type="matrix"
                            values="1 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 1 0"
                            result="r"
                        />
                        <feColorMatrix
                            in="disp1"
                            type="matrix"
                            values="0 0 0 0 0  0 1 0 0 0  0 0 0 0 0  0 0 0 1 0"
                            result="g"
                        />
                        <feColorMatrix
                            in="disp2"
                            type="matrix"
                            values="0 0 0 0 0  0 0 0 0 0  0 0 1 0 0  0 0 0 1 0"
                            result="b"
                        />
                        <feBlend in="r" in2="g" mode="screen" result="rg" />
                        <feBlend in="rg" in2="b" mode="screen" result="rgb" />
                        {/* The body of the glass: a short blur so the
                            refracted picture reads as seen through something,
                            and a lift in saturation because glass this thick
                            deepens what it transmits. */}
                        <feGaussianBlur
                            in="rgb"
                            stdDeviation={t.blur}
                            result="soft"
                        />
                        <feColorMatrix
                            in="soft"
                            type="saturate"
                            values="1.45"
                        />
                    </filter>
                </svg>
            ) : null}

            {/* The refracting body. The plain blur is the floor: it is what
                shows where SVG backdrop filters are not supported, and the
                filter simply replaces it where they are. */}
            {/* The blur, on its own layer.
                
                It used to share the refraction's layer, which meant the SVG
                filter replaced it: `backdrop-filter` takes one value, and the
                mark visibly snapped from frosted to clear the moment the maps
                finished building. Chaining the two in one value parses but does
                not render. Stacked, each does its own job — and because a
                backdrop filter samples what is painted below it, the refraction
                above reads this layer's blurred result. */}
            <span
                className="absolute inset-0 backdrop-blur-[24px] backdrop-brightness-[1.08] backdrop-saturate-[1.35]"
                style={MASK_STYLE}
            />

            <span
                className="absolute inset-0"
                style={{
                    ...MASK_STYLE,
                    ...(maps && box
                        ? {
                              backdropFilter: `url(#${filterId})`,
                              WebkitBackdropFilter: `url(#${filterId})`,
                          }
                        : null),
                }}
            />

            {/* Barely a tint. The old version leaned on this to look like
                glass, which is exactly why it read as white plastic; with the
                refraction doing the work it only has to keep the letter
                legible against a bright sky. */}
            <span
                className="absolute inset-0"
                style={{
                    ...MASK_STYLE,
                    backgroundColor: `rgba(${t.tintColor},${t.tint})`,
                }}
            />

            {/* The rim light, drawn from the same vector field: bright where
                the bezel turns towards the lamp, nothing where it turns away.
                An even outline would read as a stroke; this reads as a
                surface. */}
            {maps ? (
                <>
                    <span
                        className="absolute inset-0 mix-blend-multiply"
                        style={{
                            ...MASK_STYLE,
                            backgroundImage: `url(${maps.shade})`,
                            backgroundSize: 'contain',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat',
                        }}
                    />
                    <span
                        className="absolute inset-0 mix-blend-screen"
                        style={{
                            ...MASK_STYLE,
                            backgroundImage: `url(${maps.light})`,
                            backgroundSize: 'contain',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat',
                        }}
                    />
                </>
            ) : null}
        </span>
    );
};
